import { createEntityAdapter, createSlice, PayloadAction, Slice } from "@reduxjs/toolkit";
import { resetState } from "@somewear/model";

import { mediaCommRoomActions } from "./mediaComms.rooms.actions";
import { ROOM_TRACKS_TTL_MS } from "./mediaComms.rooms.model";
import { MediaRoom, MediaRoomTrack } from "./mediaComms.rooms.state";
import { isWorkspaceMediaRoom } from "./mediaComms.rooms.utils";

export const mediaCommRoomsAdapter = createEntityAdapter<MediaRoom, string>({
	selectId: (mediaComms) => mediaComms.name,
});

export const mediaCommRoomsSlice: Slice = createSlice({
	name: "mediaCommRooms",
	initialState: mediaCommRoomsAdapter.getInitialState(),
	reducers: {
		updateRoomTracks: (
			state,
			action: PayloadAction<{ roomName: string; incomingTracks: MediaRoomTrack[] }>,
		) => {
			const { roomName, incomingTracks } = action.payload;
			const existingRoom = state.entities[roomName];
			if (!existingRoom) return;

			const now = performance.now();
			const newTracksMap = new Map(incomingTracks.map((t) => [t.publication.trackSid, t]));

			const nextTracks: MediaRoomTrack[] = [];
			const processedTrackSids = new Set<string>();

			// Set up new tracks to be added to room and add unpublishedAt
			// for tracks that are not in the incoming tracks.
			for (const existingTrack of existingRoom.tracks) {
				const existingTrackId = existingTrack.publication.trackSid;
				const newTrackState = newTracksMap.get(existingTrackId)!;

				if (newTracksMap.has(existingTrackId) && !newTrackState.publication.isMuted) {
					nextTracks.push(newTrackState);
					processedTrackSids.add(existingTrackId);
				} else {
					nextTracks.push({
						...existingTrack,
						unpublishedAt: existingTrack.unpublishedAt ?? now,
					});
				}
			}

			// Append new tracks
			for (const track of incomingTracks) {
				if (!processedTrackSids.has(track.publication.trackSid)) {
					nextTracks.push(track);
				}
			}

			existingRoom.tracks = nextTracks;

			if (existingRoom.minimizedSnapshot !== null) {
				const snapshotSet = new Set(existingRoom.minimizedSnapshot);
				const hasNewRemoteTrack = incomingTracks.some(
					(t) => !t.isLocalTrack && !snapshotSet.has(t.publication.trackSid),
				);
				if (hasNewRemoteTrack) {
					existingRoom.minimizedSnapshot = null;
				}
			}
		},
		cullExpiredTracks: (state, action: PayloadAction<{ roomName: string }>) => {
			const { roomName } = action.payload;
			const existingRoom = state.entities[roomName];
			if (!existingRoom) return;

			const now = performance.now();
			existingRoom.tracks = existingRoom.tracks.filter((track: MediaRoomTrack) => {
				// console.warn({ existingTrack: track });
				if (!track.unpublishedAt) return true;
				if (track.isLocalTrack && track.unpublishedAt !== undefined) return false;
				return now - track.unpublishedAt <= ROOM_TRACKS_TTL_MS;
			});
		},
		setRoomMinimized: (
			state,
			action: PayloadAction<{ roomName: string; minimizedSnapshot: string[] | null }>,
		) => {
			const { roomName, minimizedSnapshot } = action.payload;
			const existingRoom = state.entities[roomName];
			if (!existingRoom) return;
			existingRoom.minimizedSnapshot = minimizedSnapshot;
		},
		setRoomMuted: (state, action: PayloadAction<{ roomName: string; isMuted: boolean }>) => {
			const { roomName, isMuted } = action.payload;
			const existingRoom = state.entities[roomName];
			if (!existingRoom) return;
			existingRoom.isMuted = isMuted;
		},
		updateRoomLastActivity: (
			state,
			action: PayloadAction<{ roomName: string; lastActivity: number }>,
		) => {
			const { roomName, lastActivity } = action.payload;
			const existingRoom = state.entities[roomName];
			if (!existingRoom) return;
			existingRoom.lastActivity = lastActivity;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(resetState, (state) => {
			mediaCommRoomsAdapter.removeAll(state);
		});
		builder.addCase(mediaCommRoomActions.getActiveMediaRooms.fulfilled, (state, action) => {
			mediaCommRoomsAdapter.upsertMany(
				state,
				action.payload.data.roomsList.map((room) => ({
					name: room.name,
					lastActivity: room.lastActivity ?? 0,
					type: isWorkspaceMediaRoom({ name: room.name } as MediaRoom)
						? "workspace"
						: "direct",
					tracks: [],
					minimizedSnapshot: null,
					isMuted: false,
				})),
			);
		});
		builder.addCase(mediaCommRoomActions.createMediaRoom.fulfilled, (state, action) => {
			const { room, token, url } = action.payload.data;
			if (!room || !token || !url) return;

			mediaCommRoomsAdapter.upsertOne(state, {
				...room,
				token,
				url,
				type: isWorkspaceMediaRoom({ name: room.name } as MediaRoom)
					? "workspace"
					: "direct",
				tracks: [],
				minimizedSnapshot: null,
				isMuted: false,
			});
		});
		builder.addCase(mediaCommRoomActions.getMediaRoomJoinToken.fulfilled, (state, action) => {
			const { room, token, url } = action.payload.data;
			if (!room) return;
			const existingRoom = state.entities[room.name];

			mediaCommRoomsAdapter.upsertOne(state, {
				...(existingRoom ?? {}),
				...room,
				token,
				url,
				isMuted: false,
				minimizedSnapshot: null,
				type: isWorkspaceMediaRoom({ name: room.name } as MediaRoom)
					? "workspace"
					: "direct",
				tracks: (existingRoom?.tracks ?? []) as MediaRoomTrack[],
			});
		});
	},
});

export const {
	updateRoomTracks,
	cullExpiredTracks,
	setRoomMinimized,
	setRoomMuted,
	updateRoomLastActivity,
} = mediaCommRoomsSlice.actions;
